/**
 * docxFieldMatcher.ts
 * Rule-based mapping of template field names to Excel headers.
 *
 * Matching priority:
 *   1. Exact name.
 *   2. Case-insensitive.
 *   3. After trimming / collapsing whitespace.
 *   4. After Vietnamese diacritic normalization.
 *
 * If more than one Excel column matches a field, the mapping is AMBIGUOUS and
 * must be resolved manually — never auto-selected.
 */

import { normalizeHeader, SYNONYM_MAP } from "./columnMatcher";
import type { DocxMarkerField } from "./docxMarker";

export type MappingStatus = "mapped" | "not_found" | "ambiguous";

export interface FieldMappingResult {
  fieldName: string;
  normalizedName: string;
  /** The chosen Excel header. Empty string when not mapped. */
  excelColumn: string;
  /** All Excel headers that matched (used to detect ambiguity). */
  matchedColumns: string[];
  status: MappingStatus;
}

export interface AmbiguityIssue {
  fieldName: string;
  matchedColumns: string[];
}

export interface MappingAudit {
  results: FieldMappingResult[];
  mappedCount: number;
  notFoundCount: number;
  ambiguousCount: number;
  /** Column names that appear more than once after normalization (duplicate headers). */
  duplicateHeaders: string[];
  issues: AmbiguityIssue[];
}

/**
 * True when two normalized names belong to the same rule-based synonym group.
 */
export function areSynonyms(a: string, b: string): boolean {
  if (!a || !b) return false;
  for (const [key, list] of Object.entries(SYNONYM_MAP)) {
    const inA = a === normalizeHeader(key) || list.includes(a);
    const inB = b === normalizeHeader(key) || list.includes(b);
    if (inA && inB) return true;
  }
  return false;
}

/**
 * Map a single field name against a list of Excel headers.
 * Returns the mapping result with a status.
 */
export function matchFieldToColumn(
  fieldName: string,
  headers: string[]
): FieldMappingResult {
  const normalizedName = normalizeHeader(fieldName);
  const excelColumn = "";
  if (!normalizedName) {
    return {
      fieldName,
      normalizedName,
      excelColumn,
      matchedColumns: [],
      status: "not_found",
    };
  }

  const matchedColumns: string[] = [];
  for (const header of headers) {
    if (normalizeHeader(header) === normalizedName) {
      matchedColumns.push(header);
    }
  }

  // Fallback: rule-based synonym matching (deterministic, not AI) so friendly
  // labels like "Tên Nhà cung cấp" still map to header "Tên NCC".
  if (matchedColumns.length === 0) {
    for (const header of headers) {
      if (areSynonyms(normalizedName, normalizeHeader(header))) {
        matchedColumns.push(header);
      }
    }
  }

  // Last resort: substring containment in either direction. Short strings are
  // skipped because they collide with almost anything.
  if (matchedColumns.length === 0 && normalizedName.length > 2) {
    for (const header of headers) {
      const normHeader = normalizeHeader(header);
      if (normHeader.length <= 2) continue;
      if (normHeader.includes(normalizedName) || normalizedName.includes(normHeader)) {
        matchedColumns.push(header);
      }
    }
  }

  if (matchedColumns.length === 1) {
    return {
      fieldName,
      normalizedName,
      excelColumn: matchedColumns[0],
      matchedColumns,
      status: "mapped",
    };
  }

  if (matchedColumns.length > 1) {
    return {
      fieldName,
      normalizedName,
      excelColumn,
      matchedColumns,
      status: "ambiguous",
    };
  }

  return {
    fieldName,
    normalizedName,
    excelColumn,
    matchedColumns: [],
    status: "not_found",
  };
}

/**
 * Auto-map a list of template fields to Excel headers with ambiguity detection.
 */
export function autoMapFields(
  fields: DocxMarkerField[] | string[],
  headers: string[]
): FieldMappingResult[] {
  const names = fields.map((f) => (typeof f === "string" ? f : f.fieldName));
  return names.map((name) => matchFieldToColumn(name, headers));
}

/** Detect duplicate Excel headers after normalization (case/diacritics/space-insensitive). */
export function detectDuplicateHeaders(headers: string[]): string[] {
  const seen = new Map<string, string[]>();
  for (const header of headers) {
    const norm = normalizeHeader(header);
    if (!norm) continue;
    const list = seen.get(norm) ?? [];
    list.push(header);
    seen.set(norm, list);
  }
  const dupes: string[] = [];
  for (const [, list] of seen) {
    if (list.length > 1) dupes.push(...list);
  }
  return dupes;
}

/** Produce a full mapping audit for a set of fields against Excel headers. */
export function auditMappings(
  fields: DocxMarkerField[],
  headers: string[]
): MappingAudit {
  const results = autoMapFields(fields, headers);
  const mappedCount = results.filter((r) => r.status === "mapped").length;
  const notFoundCount = results.filter((r) => r.status === "not_found").length;
  const ambiguousCount = results.filter((r) => r.status === "ambiguous").length;
  const issues: AmbiguityIssue[] = results
    .filter((r) => r.status === "ambiguous")
    .map((r) => ({ fieldName: r.fieldName, matchedColumns: r.matchedColumns }));

  return {
    results,
    mappedCount,
    notFoundCount,
    ambiguousCount,
    duplicateHeaders: detectDuplicateHeaders(headers),
    issues,
  };
}